import { Edit2, FilterRemove, Trash } from "iconsax-react";
import React, { useState } from "react";
import FilterTextInput from "../FilterTextInput";
import { motion as m } from "framer-motion";
import Can from "../Can";
import Paginator from "../Paginator";
import Modal from "../Modal";
import axios from "axios";
import { DELETE_ROLE_API } from "../../Constant/api";
import { useToast } from "../Toast";

const RoleListTable = ({
  data,
  meta,
  handlePageClick,
  searchTerm,
  handleSearch,
  handleKeyDown,
  perPage,
  handlePerPageChange,
  clearFilter,
  onEdit,      
  onDelete,    
  onRefresh,  
}) => {
  const { toast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const toggleModal = (roleId = null) => {
    setSelectedRoleId(roleId);
    setShowModal(!showModal);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${DELETE_ROLE_API}/${selectedRoleId}`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
      });
      toast("Role deleted successfully", "success");
      setShowModal(false);
      onRefresh();
    } catch (err) {
      console.error(err);
      toast("Failed to delete role", "error");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <m.div
      className="flex flex-col border border-[#EFF0F2] w-full h-fit bg-white rounded-xl"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {showModal && (
        <Modal toggleModal={() => toggleModal()} confirmation={handleDelete}>
          <div className="flex flex-col w-full items-center justify-center gap-[16px] py-[32px]">
            <Trash size="64" color="#ff0000" variant="Bold" />
            <p>Are you sure you want to delete this role?</p>
            {deleting && <p className="text-sm text-gray-500">Deleting...</p>}
          </div>
        </Modal>
      )}

      <div className="flex flex-col w-full h-full">
        {/* Filters */}
        <div className="flex w-full border-b border-[#EFF0F2] px-[24px] py-[12px] gap-[8px] items-center justify-end">
          {clearFilter && searchTerm !== "" && (
            <FilterRemove
              size="16"
              onClick={clearFilter}
              variant="Bold"
              className="text-[#FF3030] hover:cursor-pointer hover:text-[#5C4BF5]"
            />
          )}
          <FilterTextInput
            value={searchTerm}
            onChange={(e) => handleSearch(e)}
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className="flex w-full h-full gap-[24px] no-scrollbar overflow-x-auto">
          {!data || data.length === 0 ? (
            <div className="flex w-full h-[290px] items-center border-[#EFF0F2] justify-center text-gray-500 text-[25px]">
              No roles found
            </div>
          ) : (
            <table className="w-full h-full">
              <thead>
                <tr className="w-full bg-[#F7F8F9]">
                  <th className="text-start px-[24px] py-[16px]">Role Name</th>
                  <th className="text-start px-[24px] py-[16px]">Created At</th>
                  <th className="text-start px-[24px] py-[16px]"></th>
                </tr>
              </thead>

              <tbody>
                {data.map((item) => (
                  <tr
                    key={item.id}
                    className="w-full font-normal border-b border-[#EFF0F2] last:border-none"
                  >
                    <td className="px-[24px] py-[16px]">{item.name ?? "-"}</td>
                    <td className="px-[24px] py-[16px]">
                      {formatDate(item.created_at)}
                    </td>
                    <td className="flex px-[24px] py-[16px] h-full gap-[12px] items-center justify-end">
                      <Can permission="edit_role">
                        <span
                          className="flex text-[#5C4BF5] text-[14px] items-center hover:underline cursor-pointer"
                          onClick={() => onEdit(item.id)}
                        >
                          <Edit2 size="15" variant="Bold" />
                          Edit
                        </span>
                      </Can>
                      <Can permission="delete_role">
                        <span
                          className="flex text-red-500 text-[14px] items-center hover:underline cursor-pointer"
                          onClick={() => toggleModal(item.id)}
                        >
                          <Trash size="15" variant="Bold" />
                          Delete
                        </span>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      {meta && (
        <Paginator
          meta={meta}
          perPage={perPage}
          handlePerPageChange={handlePerPageChange}
          handlePageClick={handlePageClick}
        />
      )}
    </m.div>
  );
};

export default RoleListTable;